<template>
  <div v-if="isOpen" class="modal-backdrop" @click.self="close">
    <div class="modal-content animate-in">
      <div class="modal-header grid-x align-justify align-middle">
        <h3 class="cell auto">{{ isEditing ? 'Edit User Profile' : 'Add New User' }}</h3>
        <button class="cell shrink button-close" @click="close">&times;</button>
      </div>

      <form class="modal-body" @submit.prevent="handleSubmit">
        <div class="form-group">
          <label>First Name</label>
          <input v-model="form.firstname" type="text" placeholder="First name" />
        </div>

        <div class="form-group">
          <label>Last Name</label>
          <input v-model="form.lastname" type="text" placeholder="Last name" />
        </div>

        <div class="form-group">
          <label>Email Address</label>
          <input
            v-model="form.email"
            type="email"
            placeholder="user@blything.co.uk"
            :disabled="isEditing"
            :required="!isEditing"
          />
        </div>

        <div class="form-group">
          <label>Profile Picture URL</label>
          <input v-model="form.picture" type="url" placeholder="https://example.com/avatar.jpg" />
        </div>

        <div class="form-group">
          <label>Address Line 1</label>
          <input v-model="form.location.address_line1" type="text" placeholder="Street address" />
        </div>

        <div class="form-group">
          <label>Address Line 2</label>
          <input v-model="form.location.address_line2" type="text" placeholder="Apartment, suite, etc." />
        </div>

        <div class="form-group">
          <label>Address Line 3</label>
          <input v-model="form.location.address_line3" type="text" placeholder="City / Town" />
        </div>

        <div class="form-group grid-x grid-margin-x">
          <div class="cell medium-6">
            <label>Country</label>
            <input v-model="form.location.country" type="text" placeholder="Country" />
          </div>
          <div class="cell medium-6">
            <label>Postcode</label>
            <input v-model="form.location.postcode" type="text" placeholder="Postcode" />
          </div>
        </div>

        <div v-if="!isEditing" class="form-group">
          <label>Temporary Password</label>
          <input v-model="form.password" type="password" placeholder="Enter secure temporary password" required />
        </div>

        <div class="form-group">
          <label>Role Assignment</label>
          <select v-model="form.role">
            <option value="User">User</option>
            <option value="Admin">Admin</option>
          </select>
        </div>

        <div class="modal-footer text-right">
          <button type="button" class="btn-modal-cancel" @click="close">Cancel</button>
          <button type="submit" class="btn-modal-submit" :disabled="saving">
            {{ saving ? 'Saving Changes...' : (isEditing ? 'Save Changes' : 'Create User') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  isOpen: Boolean,
  isEditing: Boolean,
  userData: {
    type: Object,
    default: () => null
  },
  saving: Boolean
});

const emit = defineEmits(['close', 'save']);

const defaultLocation = () => ({
  address_line1: '',
  address_line2: '',
  address_line3: '',
  country: '',
  postcode: ''
});

const form = ref({
  id: '',
  name: '',
  email: '',
  picture: '',
  role: 'User',
  password: '',
  firstname: '',
  lastname: '',
  location: defaultLocation()
});

watch(() => props.userData, (newUser) => {
  if (newUser) {
    form.value = {
      ...newUser,
      location: newUser.location ? { ...newUser.location } : defaultLocation()
    };
  }
}, { immediate: true, deep: true });

const close = () => {
  emit('close');
};

const handleSubmit = () => {
  const formValues = form.value;

  const payload = {
    userId: formValues.id || formValues.userId,
    email: formValues.email,
    password: formValues.password,
    name: [formValues.firstname, formValues.lastname].filter(Boolean).join(' ').trim(),
    role: formValues.role,
    user_metadata: {
      firstname: formValues.firstname || '',
      lastname: formValues.lastname || '',
      location: {
        address_line1: formValues.location?.address_line1 || '',
        address_line2: formValues.location?.address_line2 || '',
        address_line3: formValues.location?.address_line3 || '',
        country: formValues.location?.country || '',
        postcode: formValues.location?.postcode || ''
      }
    }
  };

  if (formValues.picture && formValues.picture.trim()) {
    payload.picture = formValues.picture.trim();
  }

  emit('save', payload);
};
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "@/assets/styles/abstract/variables" as *;

$border-dark: rgba(255, 255, 255, 0.08);
$accent-cyan: #38bdf8;
$text-light: #f8fafc;
$text-muted: #94a3b8;

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(5, 7, 13, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
  overflow-y: auto;

  .modal-content {
    width: 100%;
    max-width: 520px;
    background: #0f172a;
    border-radius: 16px;
    padding: 2rem;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.1);
    max-height: 90vh;
    overflow-y: auto;

    .modal-header {
      margin-bottom: 1.5rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      padding-bottom: 0.75rem;

      h3 {
        font-size: 1.15rem;
        font-weight: 700;
        color: $text-light;
        margin: 0;
      }

      .button-close {
        background: none;
        border: none;
        font-size: 1.5rem;
        color: $text-muted;
        cursor: pointer;
        line-height: 1;

        &:hover {
          color: $text-light;
        }
      }
    }

    .modal-body {
      .form-group {
        margin-bottom: 1.15rem;

        label {
          display: block;
          font-size: 0.75rem;
          font-weight: 700;
          color: $text-muted;
          margin-bottom: 0.4rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        input,
        select {
          width: 100%;
          padding: 0.65rem 0.9rem;
          border: 1px solid $border-dark;
          border-radius: 8px;
          font-size: 0.9rem;
          color: $text-light;
          background: rgba(15, 23, 42, 0.9);
          transition: all 0.2s ease;

          &:focus {
            outline: none;
            border-color: $accent-cyan;
            background: #0f172a;
            box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.15);
          }

          &:disabled {
            background: rgba(255, 255, 255, 0.03);
            color: $text-muted;
            cursor: not-allowed;
          }

          &::placeholder {
            color: rgba(255, 255, 255, 0.25);
          }
        }
      }
    }

    .modal-footer {
      margin-top: 1.75rem;
      border-top: 1px solid rgba(255, 255, 255, 0.08);
      padding-top: 1.25rem;
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;

      button {
        border-radius: 8px;
        padding: 0.6rem 1.25rem;
        font-weight: 600;
        font-size: 0.88rem;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .btn-modal-cancel {
        background: rgba(255, 255, 255, 0.05);
        color: $text-muted;
        border: 1px solid $border-dark;

        &:hover {
          background: rgba(255, 255, 255, 0.1);
          color: $text-light;
        }
      }

      .btn-modal-submit {
        background: $accent-cyan;
        color: #0f172a;
        border: 1px solid transparent;
        font-weight: 700;

        &:hover {
          background: color.adjust($accent-cyan, $lightness: 6%);
          box-shadow: 0 0 15px rgba(56, 189, 248, 0.4);
        }
      }
    }
  }
}
</style>
